class CarpetDeodorizerBundle extends HTMLElement {
  connectedCallback() {
    this._form = document.forms[this.dataset.formId];
    if (!this._form) return;

    this._packCount = Number(this.dataset.packCount || 2);
    this._sectionEl = document.getElementById(`shopify-section-${this.dataset.sectionId}`);
    this._singlePicker = this.closest('.product-info__variant-picker')?.querySelector(
      '.carpet-bundle-pdp-single-picker',
    );
    this._errorEl = this.querySelector('[data-carpet-bundle-error]');

    const optionsScript = this.nextElementSibling?.matches?.('[data-carpet-scent-options]')
      ? this.nextElementSibling
      : this.parentElement?.querySelector('[data-carpet-scent-options]');
    try {
      this._scentOptions = optionsScript ? JSON.parse(optionsScript.textContent) : [];
    } catch (e) {
      this._scentOptions = [];
    }

    const mapScript = this.parentElement?.querySelector('[data-bundle-option-value-map]');
    try {
      this._optionValueMap = mapScript ? JSON.parse(mapScript.textContent) : null;
    } catch (e) {
      this._optionValueMap = null;
    }

    const fallbackScript = this.parentElement?.querySelector('[data-bundle-fallback-variant]');
    try {
      this._fallbackVariant = fallbackScript ? JSON.parse(fallbackScript.textContent) : null;
    } catch (e) {
      this._fallbackVariant = null;
    }

    this._slots = new Array(this._packCount).fill('');
    this._variant = null;
    this._resolveVersion = 0;

    this._template = this.querySelector('[data-carpet-scent-pill-template]');
    this._buildScentPills();
    this._onDocClickScentInfo = (e) => {
      if (e.target.closest('[data-carpet-scent-info]')) return;
      if (e.target.closest('.carpet-pack-scent-tooltip')) return;
      if (!this.querySelector('.carpet-pack-scent-tooltip.is-open')) return;
      this._closeAllScentInfoPopovers();
    };
    document.addEventListener('click', this._onDocClickScentInfo, true);
    this._onKeydownScentInfo = (e) => {
      if (e.key === 'Escape') this._closeAllScentInfoPopovers();
    };
    document.addEventListener('keydown', this._onKeydownScentInfo, true);

    this._mobileSheet = this.querySelector('[data-carpet-mobile-sheet]');
    this._mobileSheetTitle = this.querySelector('[data-carpet-mobile-sheet-title]');
    this._mobileSheetOptions = this.querySelector('[data-carpet-mobile-sheet-options]');
    this._mobileSheetBackdrop = this.querySelector('[data-carpet-mobile-sheet-backdrop]');
    this._mobileSheetClose = this.querySelector('[data-carpet-mobile-sheet-close]');
    this._mobileSheetOpen = false;
    this._mobileSheetActiveSlot = null;
    this._mobileSheetCloseTimer = null;
    this._onMobileSheetKeydown = (e) => {
      if (e.key === 'Escape' && this._mobileSheetOpen) this._closeMobileScentSheet();
    };
    this._mobileSheetBackdrop?.addEventListener('click', () => this._closeMobileScentSheet());
    this._mobileSheetClose?.addEventListener('click', () => this._closeMobileScentSheet());

    if (this._singlePicker) this._singlePicker.setAttribute('hidden', '');
    this._setBundleMode(true);

    this._form.addEventListener('submit', (e) => this._onFormSubmit(e), true);

    /* Only one scent left (e.g. Laundry Powder Peppermint) → preselect every slot. */
    if (this._applySoleScentToSlots()) {
      this._highlightPills();
      void this._resolveVariant();
    }

    this._syncBuyButtons();
  }

  _soleScentName() {
    const opts = this._scentOptions || [];
    if (opts.length !== 1) return '';
    return String(opts[0]?.name || '').trim();
  }

  _applySoleScentToSlots() {
    const sole = this._soleScentName();
    if (!sole || !this._packCount) return false;
    this._slots = new Array(this._packCount).fill(sole);
    return true;
  }

  disconnectedCallback() {
    if (this._mobileSheetHintScrollBound && this._mobileSheetOptions && this._onMobileSheetOptionsScrollHint) {
      this._mobileSheetOptions.removeEventListener('scroll', this._onMobileSheetOptionsScrollHint);
      this._mobileSheetHintScrollBound = false;
    }
    if (this._onDocClickScentInfo) {
      document.removeEventListener('click', this._onDocClickScentInfo, true);
    }
    if (this._onKeydownScentInfo) {
      document.removeEventListener('keydown', this._onKeydownScentInfo, true);
    }
    this._closeMobileScentSheet();
    if (this._mobileSheetCloseTimer) {
      clearTimeout(this._mobileSheetCloseTimer);
      this._mobileSheetCloseTimer = null;
    }
  }

  _money(cents) {
    return `$${(Number(cents || 0) / 100).toFixed(2)}`;
  }

  _scentSwatchInlineStyle(themeSwatchCss) {
    const s = (themeSwatchCss || '').trim();
    if (!s) return '';
    const sep = s.endsWith(';') ? '' : ';';
    return `${s}${sep} background: var(--swatch-background) center / cover;`;
  }

  _allSubmitButtons() {
    const fid = this._form.id;
    const fromForm = Array.from(this._form.querySelectorAll('button[type="submit"]'));
    const linked = Array.from(document.querySelectorAll(`button[type="submit"][form="${fid}"]`));
    return [...new Set([...fromForm, ...linked])];
  }

  _setBundleMode(on) {
    const target = this._sectionEl || document.documentElement;
    target.classList.toggle('carpet-pdp--bundle-mode', on);
  }

  /* ── Scent pills (desktop) ── */

  _buildScentPills() {
    if (!this._scentOptions.length) return;
    this._buildDesktopScentPills();
    this._buildMobileScentPickers();
  }

  _outOfStockLabel() {
    return this.dataset.msgOutOfStock || 'Out of stock';
  }

  _isScentOptionAvailable(opt) {
    return !opt || opt.available !== false;
  }

  _buildDesktopScentPills() {
    if (!this._template) return;
    this.querySelectorAll('[data-carpet-scent-row]').forEach((row) => {
      const slot = Number(row.getAttribute('data-carpet-scent-row'));
      row.innerHTML = '';
      this._scentOptions.forEach((opt) => {
        const frag = this._template.content.cloneNode(true);
        const btn = frag.querySelector('[data-carpet-scent-btn]');
        const sw = frag.querySelector('[data-swatch]');
        const nameEl = frag.querySelector('[data-scent-name]');
        if (!btn) return;
        if (sw) {
          if (opt.style) sw.setAttribute('style', opt.style);
          if (opt.ring) sw.classList.add('ring-inset');
        }
        if (nameEl) nameEl.textContent = opt.name;
        btn.setAttribute('data-scent', opt.name);
        const inStock = this._isScentOptionAvailable(opt);
        if (!inStock) {
          btn.classList.add('is-disabled', 'is-sold-out');
          btn.setAttribute('aria-disabled', 'true');
          btn.title = this._outOfStockLabel();
          btn.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
          });
        } else {
          btn.addEventListener('click', () => this._selectScent(slot, opt.name));
        }
        row.appendChild(frag);
      });
    });
  }

  /* ── Mobile scent pickers ── */

  _isMobileScentSheetUi() {
    return typeof window.matchMedia === 'function' && window.matchMedia('(max-width: 767px)').matches;
  }

  _measureMobileSheetScrollHint() {
    const el = this._mobileSheetOptions;
    const shell = el?.closest?.('[data-mobile-sheet-options-shell]');
    if (!(el instanceof HTMLElement && shell instanceof HTMLElement)) return;
    const { scrollTop, scrollHeight, clientHeight } = el;
    const hasOverflow = scrollHeight > clientHeight + 6;
    const atEnd = scrollTop + clientHeight >= scrollHeight - 8;
    shell.classList.toggle('is-scrollable', hasOverflow);
    shell.classList.toggle('is-at-scroll-end', !hasOverflow || atEnd);
  }

  _syncMobileSheetScrollHintAfterFill() {
    const el = this._mobileSheetOptions;
    if (!(el instanceof HTMLElement)) return;
    el.scrollTop = 0;
    const shell = el.closest('[data-mobile-sheet-options-shell]');
    if (!(shell instanceof HTMLElement)) return;

    if (!this._mobileSheetHintScrollBound) {
      this._onMobileSheetOptionsScrollHint = () => this._measureMobileSheetScrollHint();
      el.addEventListener('scroll', this._onMobileSheetOptionsScrollHint, { passive: true });
      this._mobileSheetHintScrollBound = true;
    }

    const run = () => this._measureMobileSheetScrollHint();
    queueMicrotask(() => requestAnimationFrame(() => requestAnimationFrame(run)));
  }

  _firstEmptySlot() {
    for (let i = 0; i < this._packCount; i++) {
      if (!this._slots[i]) return i;
    }
    return null;
  }

  _nextEmptySlotAfter(afterSlot) {
    for (let i = afterSlot + 1; i < this._packCount; i++) {
      if (!this._slots[i]) return i;
    }
    return null;
  }

  _scentInfoSvg() {
    return '<svg viewBox="0 0 16 16" fill="none" aria-hidden="true"><circle cx="8" cy="5" r="1.35" fill="currentColor"/><path d="M8 8.25v4.75" stroke="currentColor" stroke-width="2.1" stroke-linecap="round"/></svg>';
  }

  _createScentProfileTooltipCard() {
    const card = document.createElement('div');
    card.className = 'variant-picker__scent-profile tw-m-0';
    card.innerHTML =
      '<div class="variant-picker__scent-profile-title-row"><span class="variant-picker__scent-profile-title" data-scent-profile-title></span></div>' +
      '<p class="variant-picker__scent-profile-description" data-scent-profile-description></p>' +
      '<p class="variant-picker__scent-profile-intensity" data-scent-profile-intensity></p>' +
      '<div class="variant-picker__scent-profile-meter"><span class="variant-picker__scent-profile-meter-fill" data-scent-profile-meter></span></div>' +
      '<div class="variant-picker__scent-profile-tags" data-scent-profile-tags></div>';
    return card;
  }

  _syncScentTooltipCard(card, scentName) {
    if (typeof window.__gnbFillScentProfileCard === 'function') {
      window.__gnbFillScentProfileCard(card, scentName);
      return;
    }
    const title = card.querySelector('[data-scent-profile-title]');
    const desc = card.querySelector('[data-scent-profile-description]');
    if (title) title.textContent = scentName || '';
    if (desc) desc.textContent = this.dataset.scentDetailsFallback || '';
  }

  _closeAllScentInfoPopovers() {
    this.querySelectorAll('.carpet-pack-scent-tooltip.is-open').forEach((el) => {
      el.classList.remove('is-open');
    });
    this.querySelectorAll('[data-carpet-scent-info][aria-expanded="true"]').forEach((btn) => {
      btn.setAttribute('aria-expanded', 'false');
    });
    this._clearScentTooltipCardOverflowClass();
  }

  _clearScentTooltipCardOverflowClass() {
    this.querySelectorAll('.carpet-pack-card.carpet-pack-card--scent-tooltip-open').forEach((el) => {
      el.classList.remove('carpet-pack-card--scent-tooltip-open');
    });
  }

  _setScentTooltipCardOverflowOpen(rootEl, open) {
    this._clearScentTooltipCardOverflowClass();
    if (!open || !rootEl?.closest) return;
    const card = rootEl.closest('.carpet-pack-card');
    if (card) card.classList.add('carpet-pack-card--scent-tooltip-open');
  }

  _syncScentInfoButtonsState() {
    this.querySelectorAll('[data-carpet-scent-info]').forEach((btn) => {
      const slotStr = btn.getAttribute('data-carpet-scent-info-slot');
      if (slotStr == null || slotStr === '') return;
      const slot = Number(slotStr);
      if (Number.isNaN(slot)) return;
      const selected = (this._slots[slot] || '').trim();
      const wrap = btn.closest('.carpet-pack-mobile-scent__root');
      const tooltip = wrap?.querySelector('.carpet-pack-scent-tooltip');
      const card = tooltip?.querySelector('.variant-picker__scent-profile');
      if (!selected) {
        btn.disabled = true;
        btn.setAttribute('aria-expanded', 'false');
        tooltip?.classList.remove('is-open');
        this._clearScentTooltipCardOverflowClass();
        return;
      }
      btn.disabled = false;
      if (card && btn.getAttribute('aria-expanded') === 'true') {
        this._syncScentTooltipCard(card, selected);
      }
    });
  }

  _buildMobileScentPickers() {
    const chevronSvg =
      '<svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M4 6L8 10L12 6" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';
    const labelText = this.dataset.scentInfoLabel || 'About this scent';

    this.querySelectorAll('[data-carpet-mobile-scent]').forEach((mount) => {
      const slot = Number(mount.getAttribute('data-carpet-mobile-scent'));
      if (Number.isNaN(slot)) return;
      const placeholder = mount.dataset.placeholder || '';
      mount.innerHTML = '';

      const root = document.createElement('div');
      root.className = 'carpet-pack-mobile-scent__root';

      const row = document.createElement('div');
      row.className =
        'carpet-pack-mobile-scent__row tw-flex tw-w-full tw-min-w-0 tw-items-center tw-gap-2 tw-rounded-xl tw-border tw-border-primary-1 tw-bg-white tw-px-3 tw-py-2 md:tw-px-4 md:tw-py-3';

      const trigger = document.createElement('button');
      trigger.type = 'button';
      trigger.className =
        'carpet-pack-mobile-scent__trigger tw-flex tw-min-w-0 tw-flex-1 tw-items-center tw-gap-3 tw-text-left tw-cursor-pointer tw-border-0 tw-bg-transparent tw-p-0';
      trigger.setAttribute('aria-haspopup', 'dialog');

      const swOuter = document.createElement('span');
      swOuter.className =
        'carpet-pack-mobile-scent__swatch tw-flex tw-h-8 tw-w-8 tw-flex-shrink-0 tw-items-center tw-justify-center';
      const swDot = document.createElement('span');
      swDot.className =
        'carpet-pack-mobile-scent__swatch-dot tw-block tw-h-6 tw-w-6 tw-rounded-full tw-border tw-border-black-shades-200';
      swOuter.appendChild(swDot);

      const label = document.createElement('span');
      label.className =
        'carpet-pack-mobile-scent__label tw-min-w-0 tw-flex-1 tw-truncate tw-body-3-semibold tw-text-black-shades-400 is-placeholder';
      label.textContent = placeholder;
      label.setAttribute('data-carpet-mobile-label', '');

      trigger.append(swOuter, label);

      const infoBtn = document.createElement('button');
      infoBtn.type = 'button';
      infoBtn.className = 'carpet-pack-mobile-scent__info';
      infoBtn.setAttribute('data-carpet-scent-info', '');
      infoBtn.setAttribute('data-carpet-scent-info-slot', String(slot));
      infoBtn.setAttribute('aria-label', labelText);
      infoBtn.setAttribute('aria-expanded', 'false');
      infoBtn.disabled = true;
      infoBtn.innerHTML = this._scentInfoSvg();

      const chevBtn = document.createElement('button');
      chevBtn.type = 'button';
      chevBtn.className =
        'carpet-pack-mobile-scent__chevron-btn tw-flex tw-flex-shrink-0 tw-items-center tw-justify-center tw-text-black-shades-400 tw-border-0 tw-bg-transparent tw-p-1 tw-cursor-pointer';
      chevBtn.setAttribute('aria-haspopup', 'dialog');
      chevBtn.setAttribute('aria-label', placeholder || 'Open scent options');
      chevBtn.innerHTML = chevronSvg;
      chevBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        this._openMobileScentSheet(slot);
      });

      const tooltip = document.createElement('div');
      tooltip.className = 'carpet-pack-scent-tooltip';
      const profileCard = this._createScentProfileTooltipCard();
      tooltip.appendChild(profileCard);

      infoBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (infoBtn.disabled) return;
        const scent = (this._slots[slot] || '').trim();
        const wasOpen = tooltip.classList.contains('is-open');
        this._closeAllScentInfoPopovers();
        if (!wasOpen) {
          this._syncScentTooltipCard(profileCard, scent);
          tooltip.classList.add('is-open');
          infoBtn.setAttribute('aria-expanded', 'true');
          this._setScentTooltipCardOverflowOpen(root, true);
        }
      });

      trigger.addEventListener('click', (e) => {
        e.stopPropagation();
        this._openMobileScentSheet(slot);
      });

      row.append(trigger, infoBtn, chevBtn);
      root.appendChild(row);
      root.appendChild(tooltip);
      mount.appendChild(root);
    });
    this._syncScentInfoButtonsState();
  }

  _fillMobileSheetOptions(slot) {
    const container = this._mobileSheetOptions;
    if (!container) return;
    const selected = (this._slots[slot] || '').trim();
    container.innerHTML = '';

    this._scentOptions.forEach((opt) => {
      const row = document.createElement('button');
      row.type = 'button';
      row.className = 'carpet-pack-mobile-sheet__option';
      row.setAttribute('role', 'option');
      const name = (opt.name || '').trim();
      const isSel = name === selected && name.length > 0;
      const inStock = this._isScentOptionAvailable(opt);
      row.setAttribute('aria-selected', isSel && inStock ? 'true' : 'false');
      if (isSel && inStock) row.classList.add('is-selected');
      if (!inStock) {
        row.classList.add('is-sold-out');
        row.setAttribute('aria-disabled', 'true');
        row.disabled = true;
      }

      const oSw = document.createElement('span');
      oSw.className = 'carpet-pack-mobile-sheet__option-swatch';
      const oDot = document.createElement('span');
      oDot.className =
        'carpet-pack-mobile-sheet__swatch-dot tw-block tw-h-6 tw-w-6 tw-rounded-full tw-border tw-border-black-shades-200';
      if (opt.style) oDot.setAttribute('style', this._scentSwatchInlineStyle(opt.style));
      if (opt.ring) oDot.classList.add('ring-inset');
      oSw.appendChild(oDot);

      const oName = document.createElement('span');
      oName.className = 'carpet-pack-mobile-sheet__option-name tw-body-3-medium tw-text-primary-1';
      const oNameText = document.createElement('span');
      oNameText.className = 'carpet-pack-mobile-sheet__option-name-text';
      oNameText.textContent = opt.name;
      oName.appendChild(oNameText);

      if (!inStock) {
        const tag = document.createElement('span');
        tag.className = 'carpet-pack-mobile-sheet__oos-tag';
        tag.textContent = this._outOfStockLabel();
        oName.appendChild(tag);
      }

      const radio = document.createElement('span');
      radio.className = 'carpet-pack-mobile-sheet__radio';
      radio.setAttribute('aria-hidden', 'true');
      const radDot = document.createElement('span');
      radDot.className = 'carpet-pack-mobile-sheet__radio-dot';
      radio.appendChild(radDot);

      row.append(oSw, oName, radio);

      if (inStock) {
        row.addEventListener('click', () => {
          this._selectScent(slot, opt.name);
          this._closeMobileScentSheet();
          const next = this._nextEmptySlotAfter(slot);
          if (next != null && this._isMobileScentSheetUi()) {
            requestAnimationFrame(() => {
              requestAnimationFrame(() => this._openMobileScentSheet(next));
            });
          }
        });
      }

      container.appendChild(row);
    });

    this._syncMobileSheetScrollHintAfterFill();
  }

  _openMobileScentSheet(slot) {
    this._closeAllScentInfoPopovers();
    if (!this._mobileSheet) return;
    const mount = this.querySelector(`[data-carpet-mobile-scent="${slot}"]`);
    if (!mount) return;
    const title = mount.dataset.placeholder || '';

    if (this._mobileSheetOpen) {
      this._mobileSheetActiveSlot = slot;
      if (this._mobileSheetTitle) this._mobileSheetTitle.textContent = title;
      this._fillMobileSheetOptions(slot);
      return;
    }

    this._mobileSheetActiveSlot = slot;
    if (this._mobileSheetTitle) this._mobileSheetTitle.textContent = title;
    this._fillMobileSheetOptions(slot);

    if (this._mobileSheetCloseTimer) {
      clearTimeout(this._mobileSheetCloseTimer);
      this._mobileSheetCloseTimer = null;
    }

    this._mobileSheet.removeAttribute('hidden');
    this._mobileSheet.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', this._onMobileSheetKeydown);

    requestAnimationFrame(() => {
      this._mobileSheet.classList.add('is-visible');
      requestAnimationFrame(() => this._measureMobileSheetScrollHint());
    });

    this._mobileSheetOpen = true;
  }

  _closeMobileScentSheet() {
    if (!this._mobileSheet || !this._mobileSheetOpen) {
      document.body.style.overflow = '';
      document.removeEventListener('keydown', this._onMobileSheetKeydown);
      return;
    }

    this._mobileSheet.classList.remove('is-visible');
    this._mobileSheetOpen = false;
    this._mobileSheetActiveSlot = null;
    document.removeEventListener('keydown', this._onMobileSheetKeydown);
    document.body.style.overflow = '';

    if (this._mobileSheetCloseTimer) clearTimeout(this._mobileSheetCloseTimer);
    this._mobileSheetCloseTimer = setTimeout(() => {
      this._mobileSheet.setAttribute('hidden', '');
      this._mobileSheet.setAttribute('aria-hidden', 'true');
      this._mobileSheetCloseTimer = null;
    }, 280);
  }

  /* ── Scent selection ── */

  _selectScent(slot, scent) {
    const opt = this._scentOptions.find((o) => (o.name || '').trim() === (scent || '').trim());
    if (opt && !this._isScentOptionAvailable(opt)) return;

    this._slots[slot] = scent;
    this._highlightPills();
    this._resolveVariant();
    this._syncBuyButtons();
    this._showError('');
  }

  _highlightPills() {
    this.querySelectorAll('[data-carpet-scent-row]').forEach((row) => {
      const slot = Number(row.getAttribute('data-carpet-scent-row'));
      const selected = (this._slots[slot] || '').trim();
      row.querySelectorAll('[data-carpet-scent-btn]').forEach((btn) => {
        const val = (btn.getAttribute('data-scent') || '').trim();
        btn.classList.toggle('is-selected', val === selected && val.length > 0);
      });
      this.querySelectorAll(`[data-carpet-selected-scent-label="${slot}"]`).forEach((label) => {
        label.textContent = '';
        label.classList.add('is-empty');
      });
    });
    this._syncMobileScentTriggers();
  }

  _syncMobileScentTriggers() {
    this.querySelectorAll('[data-carpet-mobile-scent]').forEach((mount) => {
      const slot = Number(mount.getAttribute('data-carpet-mobile-scent'));
      const selected = (this._slots[slot] || '').trim();
      const placeholder = mount.dataset.placeholder || '';
      const root = mount.querySelector('.carpet-pack-mobile-scent__root');
      if (!root) return;

      const label = root.querySelector('[data-carpet-mobile-label]');
      const trig = root.querySelector('.carpet-pack-mobile-scent__trigger');
      const swDot = trig?.querySelector('.carpet-pack-mobile-scent__swatch-dot');

      if (label) {
        label.textContent = selected || placeholder;
        label.classList.toggle('is-placeholder', !selected);
        label.classList.toggle('tw-text-black-shades-400', !selected);
        label.classList.toggle('tw-text-primary-1', Boolean(selected));
      }

      if (swDot) {
        swDot.removeAttribute('style');
        swDot.classList.remove('ring-inset');
        const opt = this._scentOptions.find((o) => (o.name || '').trim() === selected);
        if (selected && opt?.style) {
          swDot.setAttribute('style', this._scentSwatchInlineStyle(opt.style));
          if (opt.ring) swDot.classList.add('ring-inset');
        }
      }
    });
    this._syncScentInfoButtonsState();
  }

  /* ── Variant resolution via Section Rendering API ── */

  async _resolveVariant() {
    this._variant = null;
    const filled = this._slots.filter(Boolean).length;
    if (filled < this._packCount) {
      this._syncPriceFromVariant();
      return;
    }

    const version = ++this._resolveVersion;

    if (this._optionValueMap) {
      const ids = [];
      for (let i = 0; i < this._packCount; i++) {
        const key = `option${i + 1}`;
        const id = this._optionValueMap[key]?.[this._slots[i]];
        if (!id) {
          this._syncPriceFromVariant();
          return;
        }
        ids.push(id);
      }

      const handle = this.dataset.productHandle;
      const root = window.Shopify?.routes?.root || '/';
      try {
        const res = await fetch(
          `${root}products/${handle}?section_id=variant-resolver&option_values=${ids.join(',')}`,
        );
        if (version !== this._resolveVersion) return;
        if (res.ok) {
          const html = await res.text();
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const jsonText = doc.querySelector('[data-variant-resolver]')?.textContent;
          this._variant = jsonText ? JSON.parse(jsonText) : null;
        }
      } catch (e) {
        if (version !== this._resolveVersion) return;
        console.error('[carpet-bundle] variant resolve error', e);
      }
    }

    if (version !== this._resolveVersion) return;
    this._syncPriceFromVariant();
    this._syncBuyButtons();
  }

  _syncPriceFromVariant() {
    const v = this._variant;
    const fid = this._form.id;
    const sale = document.querySelector('.product-info__price .buy-once-sale-price');
    const compare = document.querySelector('.product-info__price .buy-once-compare-price');
    const compareCol = document.querySelector('.product-info__price .buy-once-compare-column');
    const headerPrice = this.querySelector('[data-bundle-header-price]');
    const headerCompare = this.querySelector('[data-bundle-header-compare]');
    const headerCompareColumn = this.querySelector('[data-bundle-header-compare-column]');
    const headerBanner = this.querySelector('[data-bundle-header-banner]');
    const headerInner = this.querySelector('[data-bundle-header-inner]');
    const headerSavingAmount = this.querySelector('[data-bundle-header-saving-amount]');
    const headerPercent = this.querySelector('[data-bundle-header-percent]');

    const show = (variant) => {
      const hasSavings = variant.compare_at_price && variant.compare_at_price > variant.price;
      const savingsAmount = hasSavings ? variant.compare_at_price - variant.price : 0;
      const percentOff = hasSavings ? Math.round((savingsAmount * 100) / variant.compare_at_price) : 0;

      if (sale) sale.textContent = this._money(variant.price);
      if (compare && compareCol) {
        if (hasSavings) {
          compare.textContent = this._money(variant.compare_at_price);
          compareCol.classList.remove('tw-hidden');
        } else {
          compare.textContent = '';
          compareCol.classList.add('tw-hidden');
        }
      }

      if (headerPrice) headerPrice.textContent = this._money(variant.price);
      if (headerCompare) {
        if (hasSavings) {
          headerCompare.textContent = this._money(variant.compare_at_price);
          headerCompare.classList.remove('tw-hidden');
          headerCompare.hidden = false;
        } else {
          headerCompare.textContent = '';
          headerCompare.classList.add('tw-hidden');
          headerCompare.hidden = true;
        }
      }
      if (headerCompareColumn) {
        headerCompareColumn.classList.toggle('tw-hidden', !hasSavings);
        headerCompareColumn.hidden = !hasSavings;
      }
      if (headerBanner && headerInner) {
        headerBanner.hidden = !hasSavings;
        headerBanner.classList.toggle('tw-hidden', !hasSavings);
        headerInner.classList.toggle('tw-rounded-t-xl', !hasSavings);
      }
      if (headerSavingAmount) headerSavingAmount.textContent = this._money(savingsAmount);
      if (headerPercent) headerPercent.textContent = String(percentOff);

      if (!sale) {
        return;
      }
      document.querySelectorAll(`sale-price[form="${fid}"]`).forEach((el) => {
        const sr = el.querySelector('.sr-only');
        el.replaceChildren();
        if (sr) el.appendChild(sr);
        el.appendChild(document.createTextNode(this._money(variant.price)));
        el.classList.toggle('text-on-sale', hasSavings);
      });
      document.querySelectorAll(`compare-at-price[form="${fid}"]`).forEach((el) => {
        const hide = !hasSavings;
        el.hidden = hide;
        if (!hide) {
          const sr = el.querySelector('.sr-only');
          el.replaceChildren();
          if (sr) el.appendChild(sr);
          el.appendChild(document.createTextNode(this._money(variant.compare_at_price)));
        }
      });
    };

    if (v) {
      show(v);
      return;
    }

    if (this._fallbackVariant) show(this._fallbackVariant);
  }

  /* ── Buy button state ── */

  _syncBuyButtons() {
    const filled = this._slots.filter(Boolean).length;
    const ok = filled >= this._packCount && this._variant && this._variant.available;

    this._allSubmitButtons().forEach((btn) => {
      btn.disabled = !ok;
    });
  }

  _isCarpetTrioPack() {
    return this.dataset.productHandle === 'carpet-deodorizer-trio-pack';
  }

  _skipsGifts() {
    return this.dataset.productHandle === 'laundry-powder-trio-pack';
  }

  /* ── Form submission ── */

  _onFormSubmit(event) {
    event.preventDefault();
    event.stopImmediatePropagation();
    window.CarpetPackGifts?.markCarpetPackSubmit?.(event);

    const filled = this._slots.slice(0, this._packCount).filter(Boolean).length;
    if (filled < this._packCount) {
      this._showError(this.dataset.msgIncomplete || '');
      return;
    }

    const variant = this._variant;
    if (!variant || !variant.available) {
      this._showError(this.dataset.msgSoldOut || '');
      return;
    }

    const labels = [this.dataset.propA, this.dataset.propB, this.dataset.propC];
    const properties = { _bundle_name: this.dataset.bundleLabel || 'Bundle' };
    (variant.options || []).forEach((val, i) => {
      if (val != null && labels[i]) properties[labels[i]] = String(val);
    });

    const qtyInput = this._form.querySelector('[name="quantity"]');
    const quantity = Math.max(1, parseInt(qtyInput?.value || '1', 10) || 1);

    const root = window.Shopify?.routes?.root || '/';
    const sectionsToBundle = ['variant-added'];

    const buttons = this._allSubmitButtons();
    buttons.forEach((b) => {
      b.setAttribute('disabled', 'disabled');
      b.setAttribute('aria-busy', 'true');
    });

    const gifts = window.CarpetPackGifts;
    if (!gifts?.addBundleWithGifts) {
      this._showError('Could not add to cart.');
      buttons.forEach((b) => {
        b.removeAttribute('disabled');
        b.removeAttribute('aria-busy');
      });
      console.error('[carpet-bundle] CarpetPackGifts module not loaded');
      return;
    }

    const skipsGifts = this._skipsGifts();
    const isCarpetTrio = this._isCarpetTrioPack();

    gifts
      .addBundleWithGifts({
        bundleItem: { quantity, id: variant.id, properties },
        apcVariantId: this.dataset.rewardApcVariantId,
        deoVariantId: this.dataset.rewardDeoVariantId,
        comboVariantId: this.dataset.rewardComboVariantId,
        includeApc: !skipsGifts && !isCarpetTrio && this._packCount >= 2,
        includeDeo: false,
        includeCombo: !skipsGifts && isCarpetTrio && this._packCount >= 3,
        quantity,
        sectionsToBundle,
        sectionsUrl: `${root}variants/${variant.id}`,
      })
      .then(() => {
        if (
          window.themeVariables?.settings?.cartType === 'page' ||
          window.themeVariables?.settings?.pageType === 'cart'
        ) {
          window.location.href = `${root}cart`;
        }
      })
      .catch((e) => {
        console.error('[carpet-bundle]', e);
        this._showError(e.message || 'Could not add to cart.');
      })
      .finally(() => {
        buttons.forEach((b) => {
          b.removeAttribute('disabled');
          b.removeAttribute('aria-busy');
        });
      });
  }

  _showError(msg) {
    if (!this._errorEl) return;
    this._errorEl.textContent = msg || '';
    this._errorEl.classList.toggle('tw-hidden', !msg);
    this._errorEl.hidden = !msg;
  }
}

if (!window.customElements.get('carpet-deodorizer-bundle')) {
  window.customElements.define('carpet-deodorizer-bundle', CarpetDeodorizerBundle);
}
